import { apiFetch } from "./client";
import type { Page } from "./admin";

export type DeliveryStatus =
  | "ANNOUNCED"
  | "CONFIRMED"
  /** Truck at the gate, sample taken, laboratory working. Nothing weighed yet. */
  | "SAMPLED"
  /** The laboratory found the load non-conforming and stopped. Waiting for the desk or the director. */
  | "LAB_REJECTED"
  | "RECEIVED"
  | "SETTLED"
  | "REJECTED"
  /** Intake order withdrawn while the truck was still expected. Terminal, and not a refusal. */
  | "CANCELLED";

/**
 * Isporuke i kvalitet (supplier deliveries) — the inbound counterpart of
 * `Order`. Mirrors `DeliveryDtos.DeliveryView` on the backend exactly.
 *
 * Note: unlike `Order`, the backend does NOT expose a per-delivery status
 * history (no `history`/`statusHistory` field, no dedicated endpoint) even
 * though `DeliveryStatusHistory` rows are written server-side on every
 * transition. The UI timeline is therefore derived from `status` (+ which
 * data fields are populated) rather than a true chronological event list.
 */
export type Delivery = {
  id: string;
  companyId: string;
  companyName: string;
  procurementContractId: string | null;
  /** Prazno na uvezenom prijemu: istorija iz starog ERP-a najavu nosi samo za manji deo kamiona. */
  announcedDate: string | null;
  estimatedQuantityT: number | null;
  /** Dan kad je roba primljena; prazno dok se kamion jos ocekuje. */
  receivedOn: string | null;
  status: DeliveryStatus;
  grossKg: number | null;
  tareKg: number | null;
  netKg: number | null;
  /** Valuta iznosa obracuna. Stoji uz iznos, jer ulazni racuni nose i EUR i RSD. */
  settledCurrency: string | null;
  /** What the supplier's dispatch note says was loaded — recorded beside the weighed net, never in place of it. */
  dispatchNoteQtyKg: number | null;
  /** The intake lot this delivery went into, once somebody assigned it. */
  receivingLotId: string | null;
  /** When the intake order was withdrawn, and why. The supplier reads both. */
  cancelledAt: string | null;
  cancelReason: string | null;
  carrierName: string | null;
  vehiclePlate: string | null;
  trailerPlate: string | null;
  driverName: string | null;
  weighbridgeTicketNo: string | null;
  gateInAt: string | null;
  gateOutAt: string | null;
  /** Measured parameters, ordered as the codebook orders them. Empty until the lab records anything. */
  qualityValues: QualityValue[];
  priceCorrectionPct: number | null;
  settledAmount: number | null;
  /** What the rules proposed, kept beside what was actually settled. */
  proposedCorrectionPct: number | null;
  proposedAmount: number | null;
  settlementOverrideReason: string | null;
  rejectReason: string | null;
  /** The laboratory's finding. Present means the lab stopped the load; the decision may still be open. */
  labRejectReason: string | null;
  labRejectedAt: string | null;
  rejectConfirmedAt: string | null;
  /** The managing director's waiver: the finding stands, and the load went on anyway. */
  rejectExceptionReason: string | null;
  rejectExceptionAt: string | null;
  /** When the sample was taken — the step that comes before the weighbridge. */
  sampledAt: string | null;
  /** The class decided before unloading — what sent the lorry to its silo. */
  provisionalClassId: string | null;
  provisionalClassAt: string | null;
  /** The class once every result was in. It decides what the material may be used for. */
  finalClassId: string | null;
  finalClassAt: string | null;
  /** Required when the final class differs: the only record that goods went into another class's silo. */
  classChangeReason: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Whether the otkup desk's actions on this delivery are the caller's to take
   * (confirm, transport, weighbridge, settlement, rejection). The desk reads
   * every supplier's deliveries but acts only on its assigned suppliers'. */
  canAct: boolean;
  /** Whether the transport details may still change — false once the gate has weighed the truck. */
  transportEditable: boolean;
  /**
   * Where the delivery was recorded. A receipt from the ERP is settled there, on the final incoming
   * invoice, and the laboratory keeps its findings on the lot, so the portal takes neither on it.
   */
  origin: DeliveryOrigin;
  /** The ERP lot an imported receipt went into, where its findings are; empty for the supplier. */
  goodsLotId: string | null;
};

/** `ERP`: a receipt imported from the ERP; `PORTAL`: a delivery opened in the portal. */
export type DeliveryOrigin = "ERP" | "PORTAL";

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export type ProposalLine = {
  parameterId: string;
  measured: number;
  baseValue: number;
  correctionPct: number;
};

export type ProposalWarning = {
  parameterId: string;
  kind: "BELOW_MIN_ACCEPTED" | "ABOVE_MAX_ACCEPTED";
  measured: number;
  limit: number;
};

/** What the configured rules make of a delivery — a proposal, never a decision. */
export type SettlementProposal = {
  baseAmount: number;
  correctionPct: number;
  amount: number;
  currency: string;
  pricePerT: number;
  ruleSetId: string | null;
  lines: ProposalLine[];
  warnings: ProposalWarning[];
};

/** How a test's result is written down — the parameter decides, not the person entering it. */
export type QualityResultType = "NUMERIC" | "BOOLEAN" | "TEXT";

/** Whether a finding still needs the head of the laboratory to sign it. */
export type QualityFindingStatus = "RECORDED" | "PENDING_VERIFICATION" | "VERIFIED";

/**
 * One quality finding on a delivery. Exactly one of `value`, `valueBool` and
 * `valueText` is filled, and `resultType` says which — GMO is a yes/no read off a
 * test strip, moisture is a figure.
 *
 * `outOfRange` has three answers. True: outside the acceptance limit. False: judged
 * against a limit and inside it. **Null: nothing judged it** — no limit is prescribed
 * for this test at intake, or the finding is not a figure. The screen must say "not
 * judged" for null and never "within limits".
 */
export type QualityValue = {
  parameterId: string;
  parameterKey: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  decimals: number;
  sortOrder: number;
  resultType: QualityResultType | null;
  value: number | null;
  valueBool: boolean | null;
  valueText: string | null;
  status: QualityFindingStatus | null;
  outOfRange: boolean | null;
  recordedByName: string | null;
  recordedAt: string | null;
  verifiedByName: string | null;
  verifiedAt: string | null;
};

/** One finding the lab reports. Shape per the parameter; reason and explanation only when correcting. */
export type QualityValueEntry = {
  parameterId: string;
  value?: number | null;
  valueBool?: boolean | null;
  valueText?: string | null;
  reason?: string | null;
  explanation?: string | null;
};

/** The finding as a person reads it, whichever of the three shapes it has. */
export function displayFinding(q: QualityValue): string {
  if (q.value != null) return q.value.toFixed(q.decimals);
  if (q.valueBool != null) return q.valueBool ? "da" : "ne";
  return q.valueText ?? "—";
}

/**
 * One correction of a finding: what it replaced, who had entered it, who corrected it,
 * and the two things a correction has to state — the reason and the explanation.
 *
 * Both findings come as text, because either may be a figure, a yes/no or words.
 */
export type QualityValueChange = {
  parameterId: string;
  parameterKey: string | null;
  nameI18n: Record<string, string> | null;
  unit: string | null;
  decimals: number;
  previousValue: string;
  previousRecordedByName: string | null;
  previousRecordedAt: string;
  newValue: string;
  changedByName: string | null;
  changedAt: string;
  reason: string | null;
  explanation: string | null;
};

export type DeliveryDocumentKind =
  | "COA"
  | "LAB_REPORT"
  | "WEIGHBRIDGE_TICKET"
  | "DELIVERY_NOTE"
  | "OTHER";

export type DeliveryDocument = {
  id: string;
  kind: DeliveryDocumentKind;
  /** Null when the document exists on paper only — externalReference carries its number. */
  objectKey: string | null;
  externalReference: string | null;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  uploadedBy: string | null;
  createdAt: string;
};

/** Mirrors DeliveryDocumentService: 25 MB, 10 per delivery, PDF + images. */
export const DELIVERY_DOC_MAX_BYTES = 25 * 1024 * 1024;
export const DELIVERY_DOC_MAX_FILES = 10;
export const DELIVERY_DOC_ALLOWED = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

/**
 * Which kinds a role may attach — mirrors the server-side matrix so the UI shows
 * only the buttons that will actually work. The server decides; this just avoids
 * offering a button that returns 403. The otkup desk files papers only on a
 * delivery it may act on (`deskMayAct`, the delivery's `canAct`); the lab and the
 * supplier's own people are not narrowed by the assignment.
 */
export function attachableKinds(has: (role: string) => boolean, deskMayAct: boolean): DeliveryDocumentKind[] {
  if (has("ADMIN") || has("SUPERADMIN")) {
    return ["COA", "LAB_REPORT", "WEIGHBRIDGE_TICKET", "DELIVERY_NOTE", "OTHER"];
  }
  if (has("KVALITET")) return ["COA", "LAB_REPORT"];
  if (has("OTKUP")) return deskMayAct ? ["WEIGHBRIDGE_TICKET", "OTHER", "DELIVERY_NOTE"] : [];
  if (has("SUPP_MANAGER")) return ["DELIVERY_NOTE"];
  return [];
}

/**
 * How the ERP settled this intake.
 *
 * `amountForThisDelivery` is set only when the invoice carries one line, because only then does the
 * price belong to this intake's article beyond doubt. `coveredReceipts` says how many receipts the
 * invoice pays for: without it, the invoice amount beside one intake reads as that intake's.
 */
export type DeliverySettlementLine = {
  productName: string | null;
  unit: string | null;
  quantity: number | null;
  unitPrice: number | null;
  netAmount: number | null;
};

export type DeliverySettlement = {
  invoiceId: string;
  invoiceNumber: string | null;
  issueDate: string | null;
  currency: string | null;
  documentType: string | null;
  coveredReceipts: number;
  resolvedReceipts: number;
  amountForThisDelivery: number | null;
  /**
   * The invoice names this receipt with the partial mark: it pays for only part of it, another
   * invoice for the rest. No amount is attributed to the receipt then.
   */
  partial: boolean;
  lines: DeliverySettlementLine[];
};

export const deliveriesApi = {
  /** The desk takes back its own intake order while the truck is still expected. Reason required. */
  cancel: (id: string, reason: string) =>
    apiFetch<Delivery>(`/deliveries/${id}/cancel`, { method: "POST", body: { reason } }),
  /**
   * Spisak isporuka, po stranama.
   *
   * Veličina se uvek šalje, i kad je podrazumevana: istorija prijema iz starog ERP-a je 22.182 kamiona,
   * a red nosi preko pedeset polja. Pozivaoci kojima treba samo nekoliko redova — kontrolna tabla,
   * birač na nalogu prevoza — traže malu stranu, umesto da povuku ceo registar pa ga seku u pregledaču.
   */
  list: (
    params: {
      companyId?: string;
      status?: DeliveryStatus;
      contractId?: string;
      from?: string;
      to?: string;
      /** Free text: weighbridge slip, ERP receipt number, plate, or supplier name. */
      q?: string;
      /** Where the delivery was recorded; `PORTAL` leaves out the receipts imported from the ERP. */
      origin?: DeliveryOrigin;
    } = {},
    page = 0,
    size = 25,
  ) =>
    apiFetch<Page<Delivery>>(`/deliveries${q({ ...params, page: String(page), size: String(size) })}`),
  get: (id: string) => apiFetch<Delivery>(`/deliveries/${id}`),
  /** The otkup desk opens the announcement for a named supplier — Bankom's intake order, not the supplier's request. */
  announce: (body: {
    companyId: string;
    procurementContractId?: string;
    announcedDate: string;
    estimatedQuantityT: number;
    // The gate knows the truck by these two, so they ride along from the start.
    vehiclePlate: string;
    driverName: string;
    carrierName?: string;
    trailerPlate?: string;
  }) => apiFetch<Delivery>("/deliveries", { method: "POST", body }),
  /** The desk may still correct these until the gate weighs the truck; the plate
   * and the driver may be replaced but not emptied. */
  updateTransport: (
    id: string,
    body: { carrierName?: string; vehiclePlate?: string; trailerPlate?: string; driverName?: string },
  ) => apiFetch<Delivery>(`/deliveries/${id}/transport`, { method: "PUT", body }),
  confirm: (id: string) => apiFetch<Delivery>(`/deliveries/${id}/confirm`, { method: "POST" }),
  /** The sample is taken at the gate; the laboratory starts working and nothing is weighed yet. */
  sample: (id: string) => apiFetch<Delivery>(`/deliveries/${id}/sample`, { method: "POST" }),
  recordReceipt: (
    id: string,
    body: {
      grossKg: number;
      tareKg: number;
      dispatchNoteQtyKg?: number | null;
      weighbridgeTicketNo?: string | null;
      gateInAt?: string | null;
      gateOutAt?: string | null;
    },
  ) =>
    apiFetch<Delivery>(`/deliveries/${id}/receipt`, { method: "POST", body }),
  listDocuments: (id: string) => apiFetch<DeliveryDocument[]>(`/deliveries/${id}/documents`),
  presignDocument: (
    id: string,
    body: { fileName: string; contentType: string; sizeBytes: number; kind: DeliveryDocumentKind },
  ) => apiFetch<{ objectKey: string; url: string }>(`/deliveries/${id}/documents/presign`, { method: "POST", body }),
  confirmDocument: (
    id: string,
    body: {
      objectKey: string;
      fileName: string;
      contentType: string;
      sizeBytes: number;
      kind: DeliveryDocumentKind;
    },
  ) => apiFetch<DeliveryDocument>(`/deliveries/${id}/documents/confirm`, { method: "POST", body }),
  recordDocumentReference: (id: string, body: { kind: DeliveryDocumentKind; externalReference: string }) =>
    apiFetch<DeliveryDocument>(`/deliveries/${id}/documents/reference`, { method: "POST", body }),
  documentDownloadUrl: (id: string, documentId: string) =>
    apiFetch<{ url: string }>(`/deliveries/${id}/documents/${documentId}/download-url`),
  deleteDocument: (id: string, documentId: string) =>
    apiFetch<void>(`/deliveries/${id}/documents/${documentId}`, { method: "DELETE" }),

  /**
   * The findings the lab reports — a list, because a finding is no longer a number and
   * each one carries its own shape. Which parameters exist is a codebook, not a schema.
   */
  recordQuality: (id: string, body: { values: QualityValueEntry[] }) =>
    apiFetch<Delivery>(`/deliveries/${id}/quality`, { method: "POST", body }),
  /** The head of the laboratory signs one finding. Never the person who recorded it. */
  verifyQualityValue: (id: string, parameterId: string) =>
    apiFetch<Delivery>(`/deliveries/${id}/quality/${parameterId}/verify`, { method: "POST" }),
  /** The class decided before unloading, from the fast parameters. It picks the silo. */
  assignProvisionalClass: (id: string, classId: string) =>
    apiFetch<Delivery>(`/deliveries/${id}/provisional-class`, { method: "POST", body: { classId } }),
  /** The class once every result is in. The reason is required only when it differs. */
  assignFinalClass: (id: string, classId: string, changeReason?: string | null) =>
    apiFetch<Delivery>(`/deliveries/${id}/final-class`, { method: "POST", body: { classId, changeReason } }),
  /** The invoice that settled this intake. Empty while the intake is not yet invoiced. */
  erpSettlement: (id: string) => apiFetch<DeliverySettlement[]>(`/deliveries/${id}/erp-settlement`),
  /** Corrections of the readings, newest first — Bankom's staff only. */
  qualityChanges: (id: string) => apiFetch<QualityValueChange[]>(`/deliveries/${id}/quality-changes`),
  /** The rules' opinion. pricePerT is required only when no contract price applies. */
  settlementProposal: (id: string, pricePerT?: number) =>
    apiFetch<SettlementProposal>(
      `/deliveries/${id}/settlement-proposal${pricePerT != null ? `?pricePerT=${pricePerT}` : ""}`,
    ),
  settle: (
    id: string,
    body: {
      priceCorrectionPct: number;
      settledAmount: number;
      overrideReason?: string | null;
      manualPricePerT?: number | null;
    },
  ) =>
    apiFetch<Delivery>(`/deliveries/${id}/settle`, { method: "POST", body }),
  // Refusing a load is two steps and two roles, so it is three calls and not one.
  // The lab records the finding; the desk confirms it; the managing director waives it.
  /** The laboratory records that the goods are non-conforming. The reason is required. */
  labReject: (id: string, reason: string) =>
    apiFetch<Delivery>(`/deliveries/${id}/lab-reject`, { method: "POST", body: { reason } }),
  /** The buy-in desk confirms the refusal; the supplier is told and the register gets a row. */
  confirmRejection: (id: string) =>
    apiFetch<Delivery>(`/deliveries/${id}/reject-confirm`, { method: "POST" }),
  /** The managing director waives the refusal and the load returns to the weighbridge. */
  approveRejectionException: (id: string, reason: string) =>
    apiFetch<Delivery>(`/deliveries/${id}/reject-exception`, { method: "POST", body: { reason } }),
  /** How many loads each supplier has had turned back, and when the last one was. */
  rejectionTally: (params: { from?: string; to?: string } = {}) =>
    apiFetch<SupplierRejectionTally[]>(`/deliveries/rejections${q(params)}`),
  /** One supplier's refusals, newest first. */
  rejectionsForSupplier: (companyId: string) =>
    apiFetch<SupplierRejection[]>(`/deliveries/rejections/${companyId}`),
};

/**
 * One refused truck. There is deliberately no quantity: a refused load is turned back
 * before the weighbridge, so nothing about it was ever weighed.
 */
export type SupplierRejection = {
  id: string;
  companyId: string;
  companyName: string | null;
  deliveryId: string | null;
  rejectedOn: string;
  culture: string;
  vehiclePlate: string | null;
  reason: string;
  recordedAt: string;
};

/** A supplier's refusal record: how many, and when the last one was. No tonnage. */
export type SupplierRejectionTally = {
  companyId: string;
  companyName: string | null;
  rejections: number;
  lastRejectedOn: string | null;
};

/**
 * Mirrors the backend `DeliveryStatus.nextAllowed` exactly.
 *
 * The sample comes before the weighbridge, so SAMPLED sits between CONFIRMED and
 * RECEIVED. A refusal has one entrance only — the laboratory's finding — and from
 * there it is either confirmed or waived back to the weighbridge.
 */
export function nextAllowedDelivery(from: DeliveryStatus): DeliveryStatus[] {
  switch (from) {
    case "ANNOUNCED":    return ["CONFIRMED", "CANCELLED"];
    case "CONFIRMED":    return ["SAMPLED", "CANCELLED"];
    case "SAMPLED":      return ["RECEIVED", "LAB_REJECTED"];
    case "LAB_REJECTED": return ["REJECTED", "SAMPLED"];
    case "RECEIVED":     return ["SETTLED"];
    default:             return [];
  }
}

/**
 * LAB_REJECTED is a warning and not an error: the finding is in, the decision is
 * not. Only a confirmed refusal reads as one.
 */
export function statusBadgePalette(status: DeliveryStatus): { bg: string; fg: string } {
  switch (status) {
    // Withdrawn is administrative, not an error: the same neutral pair an order cancellation uses.
    case "CANCELLED":    return { bg: "var(--surface-muted)", fg: "var(--ink-3)" };
    case "ANNOUNCED":    return { bg: "var(--info-bg)", fg: "var(--info)" };
    case "CONFIRMED":    return { bg: "var(--warn-bg)", fg: "var(--warn)" };
    case "SAMPLED":      return { bg: "var(--tone-violet-bg)", fg: "var(--tone-violet)" };
    case "LAB_REJECTED": return { bg: "var(--tone-orange-bg)", fg: "var(--tone-orange)" };
    case "RECEIVED":     return { bg: "var(--tone-indigo-bg)", fg: "var(--tone-indigo)" };
    case "SETTLED":      return { bg: "var(--ok-bg)", fg: "var(--ok)" };
    case "REJECTED":     return { bg: "var(--err-bg)", fg: "var(--err)" };
  }
  // A state the browser does not know is a state the server added since this bundle was built,
  // which is exactly what a reader with an open tab sees during a release. An unknown state is
  // shown plainly rather than taking the screen down with it.
  return { bg: "var(--surface-2)", fg: "var(--ink-2)" };
}
